import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { motion } from 'framer-motion';
import { Trophy, Target, Flame, Crown, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    quizScores: [],
    totalPontos: 0,
    totalQuizzes: 0
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const scoresResponse = await api.getQuizScores();
      const scores = scoresResponse.data;
      const totalPontos = scores.reduce((sum, s) => sum + s.pontos_ganhos, 0);
      setStats({
        quizScores: scores,
        totalPontos,
        totalQuizzes: scores.length
      });
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const categories = [
    {
      title: 'Quiz Interativo',
      description: 'Teste seus conhecimentos',
      icon: Target,
      color: 'from-blue-500 to-cyan-500',
      image: 'https://images.unsplash.com/photo-1578269174936-2709b6aeb913?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHwzZCUyMHJlbmRlciUyMGdvbGQlMjB0cm9waHklMjBjdXAlMjB3aW5uZXJ8ZW58MHx8fHwxNzcwMDE3NDI5fDA&ixlib=rb-4.1.0&q=85',
      path: '/quiz'
    },
    {
      title: 'Caça-Palavras',
      description: 'Encontre as palavras escondidas',
      icon: TrendingUp,
      color: 'from-purple-500 to-pink-500',
      image: 'https://images.unsplash.com/photo-1582645798618-e5588fedd80c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHwzZCUyMHJlbmRlciUyMHNjaWVuY2UlMjBhdG9tJTIwbWljcm9zY29wZSUyMGljb258ZW58MHx8fHwxNzcwMDE3NDM4fDA&ixlib=rb-4.1.0&q=85',
      path: '/caca-palavras'
    },
    {
      title: 'Vídeos',
      description: 'Aprenda com conteúdo exclusivo',
      icon: Flame,
      color: 'from-orange-500 to-red-500',
      image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?q=80&w=1000&auto=format&fit=crop',
      path: '/videos'
    }
  ];

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-4 md:p-8 max-w-7xl mx-auto"
      >
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Olá, {user?.nome?.split(' ')[0] || 'Estudante'}! 👋
          </h1>
          <p className="text-muted-foreground text-lg">Pronto para aprender algo novo hoje?</p>
          {user?.is_admin && (
            <Link to="/admin" className="inline-block mt-4">
              <div className="px-4 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold hover:opacity-90 transition-opacity">
                🛠️ Acessar Painel Admin
              </div>
            </Link>
          )}
        </div>

        {user && user.plan !== 'PREMIUM' && (
          <Link to="/upgrade">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              data-testid="dashboard-premium-banner"
              className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-primary to-secondary text-white cursor-pointer hover:opacity-90 transition-opacity"
            >
              <div className="flex items-center gap-4">
                <Crown className="h-12 w-12" />
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-1">Desbloqueie o Premium</h3>
                  <p className="text-white/90">Acesso ilimitado a todo conteúdo por apenas R$ 19,90/mês</p>
                </div>
                <div className="hidden md:block px-6 py-3 bg-white text-primary rounded-full font-semibold">
                  Assinar Agora
                </div>
              </div>
            </motion.div>
          </Link>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1 }}
            data-testid="dashboard-stat-pontos"
            className="p-6 rounded-2xl bg-white border border-border/50"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Trophy className="h-5 w-5 text-primary" />
              </div>
              <span className="text-sm text-muted-foreground font-medium">Total de Pontos</span>
            </div>
            <p className="text-3xl font-bold">{stats.totalPontos}</p>
          </motion.div>

          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            data-testid="dashboard-stat-quizzes"
            className="p-6 rounded-2xl bg-white border border-border/50"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-xl bg-secondary/10 flex items-center justify-center">
                <Target className="h-5 w-5 text-secondary" />
              </div>
              <span className="text-sm text-muted-foreground font-medium">Quizzes Completos</span>
            </div>
            <p className="text-3xl font-bold">{stats.totalQuizzes}</p>
          </motion.div>

          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3 }}
            data-testid="dashboard-stat-plano"
            className="p-6 rounded-2xl bg-white border border-border/50"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
                <Flame className="h-5 w-5 text-orange-500" />
              </div>
              <span className="text-sm text-muted-foreground font-medium">Seu Plano</span>
            </div>
            <p className="text-3xl font-bold">{user?.plan === 'PREMIUM' ? 'Premium' : 'Grátis'}</p>
          </motion.div>
        </div>

        <div>
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Começar a Aprender</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {categories.map((category, index) => (
              <Link key={index} to={category.path}>
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 * index }}
                  whileHover={{ scale: 1.02 }}
                  data-testid={`dashboard-category-${category.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="relative overflow-hidden rounded-2xl aspect-[4/3] group cursor-pointer"
                >
                  <img
                    src={category.image}
                    alt={category.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <div className={`inline-flex h-12 w-12 rounded-xl bg-gradient-to-br ${category.color} items-center justify-center mb-3`}>
                      <category.icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-1">{category.title}</h3>
                    <p className="text-white/80 text-sm">{category.description}</p>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </motion.div>
    </Layout>
  );
}
